import {
  AppliedFundedOfferDetails,
  FreeDeliveryOffer,
  FundSource
} from 'src/apollo/onlineOrdering'

import { Cart, CompletedOrder } from 'public/components/online_ordering/types'

// Derived from the query result rather than the schema so these helpers only see
// the fields the cart query actually selects.
type AppliedFundedOffers = NonNullable<Cart>['appliedFundedOffers']
export type AppliedFundedOffer = NonNullable<AppliedFundedOffers>[number]

// Typed against the schema union so a rename fails the build here.
const FREE_DELIVERY_DETAILS_TYPENAME: NonNullable<
  AppliedFundedOfferDetails['__typename']
> = 'AppliedFreeDeliveryOfferDetails'

// Offer GUIDs differ per environment; requires `details { __typename }` in the query.
export const isAppliedFreeDeliveryOffer = (offer: AppliedFundedOffer) =>
  offer.details?.__typename === FREE_DELIVERY_DETAILS_TYPENAME

// The backend does not price funded offers into the order; callers subtract this.
export const getAppliedFundedOffersTotal = (offers: AppliedFundedOffers) =>
  (offers ?? []).reduce((total, offer) => total + offer.amount, 0)

export const getAppliedFreeDeliveryAmount = (offers: AppliedFundedOffers) =>
  getAppliedFundedOffersTotal(offers?.filter(isAppliedFreeDeliveryOffer))

export const getNonFreeDeliveryFundedOffersTotal = (
  offers: AppliedFundedOffers
) =>
  getAppliedFundedOffersTotal(
    offers?.filter((offer) => !isAppliedFreeDeliveryOffer(offer))
  )

// Keyed on the funder rather than the offer type, so a boost offer type added to
// the backend later needs no change here. Only one can be applied at a time.
export const getAppliedBoostOfferGuid = (offers: AppliedFundedOffers) =>
  offers?.find((offer) => offer.fundingSource === FundSource.Boost)?.offerGuid

// A placed order reports the same offers as a different schema type:
// AppliedToastFundedOffer carries the offer itself plus a Money amount and the
// payment it settled as, where the cart's AppliedFundedOffer carries `details`
// and a Float. The discriminator each type exposes is the only one available on
// it, so the free delivery checks stay side by side here rather than shared.
type AppliedToastFundedOffers =
  NonNullable<CompletedOrder>['appliedToastFundedOffers']
export type AppliedToastFundedOffer =
  NonNullable<AppliedToastFundedOffers>[number]

const FREE_DELIVERY_OFFER_TYPENAME: NonNullable<
  FreeDeliveryOffer['__typename']
> = 'FreeDeliveryOffer'

export const isFreeDeliveryToastFundedOffer = (
  offer: AppliedToastFundedOffer
) => offer.offer.__typename === FREE_DELIVERY_OFFER_TYPENAME

// Money.amount is a Decimal scalar, so it arrives as a string.
const toastFundedOfferAmount = (offer: AppliedToastFundedOffer) =>
  Number(offer.amount.amount) || 0

// The union member name is the offer type, so an offer type added to the backend
// later reports itself here without a change. Not narrowed to the amount: an
// offer applies with amount 0 when delivery was already free, and that order
// still used the offer.
export const getToastFundedOfferTypes = (offers: AppliedToastFundedOffers) => [
  ...new Set(
    (offers ?? []).flatMap((offer) =>
      offer.offer.__typename ? [offer.offer.__typename] : []
    )
  )
]

export const getCompletedOrderFreeDeliveryAmount = (
  offers: AppliedToastFundedOffers
) =>
  (offers ?? []).reduce(
    (total, offer) => total + toastFundedOfferAmount(offer),
    0
  )

// Every funded offer is a payment against the check, so without this it would
// surface in the Payments section with none of a card's details filled in. Not
// narrowed to free delivery: no Toast credit is ever the guest's own payment.
export const getFundedOfferPaymentGuids = (offers: AppliedToastFundedOffers) =>
  new Set((offers ?? []).map((offer) => offer.paymentGuid))

export type CompletedOrderPayments = NonNullable<CompletedOrder>['payments']

export const isFundedOfferInTotal = (
  offers: AppliedToastFundedOffers,
  payments: CompletedOrderPayments | undefined
) => {
  const offerPaymentGuids = getFundedOfferPaymentGuids(offers)
  return (payments ?? []).some((payment) => offerPaymentGuids.has(payment.guid))
}
